import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";

/**
 * SearchableSelect — single-select combobox with type-to-filter.
 * options: array of { value, label, hint? }
 * value: selected value (string) or "" for none
 */
export function SearchableSelect({
  options = [],
  value = "",
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No matches.",
  clearable = true,
  disabled = false,
  testId,
  className,
}) {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((o) => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", !selected && "text-muted-foreground", className)}
          data-testid={testId}
        >
          <span className="truncate">{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown size={14} className="ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {clearable && (
                <CommandItem
                  value=""
                  onSelect={() => { onChange?.(""); setOpen(false); }}
                  className="text-muted-foreground"
                >
                  <span className="opacity-60">— None —</span>
                </CommandItem>
              )}
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={`${o.label} ${o.hint || ""}`}
                  onSelect={() => { onChange?.(o.value); setOpen(false); }}
                  data-testid={testId ? `${testId}-option-${o.value}` : undefined}
                >
                  <Check size={14} className={cn("mr-2", value === o.value ? "opacity-100" : "opacity-0")} />
                  <span>{o.label}</span>
                  {o.hint && <span className="ml-auto text-xs text-muted-foreground">{o.hint}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * MultiSearchableSelect — multi-select combobox with chips.
 * value: array of selected value strings
 */
export function MultiSearchableSelect({
  options = [],
  value = [],
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No matches.",
  disabled = false,
  testId,
  className,
  maxBadges = 4,
}) {
  const [open, setOpen] = React.useState(false);
  const selectedSet = new Set(value);
  const selectedOptions = options.filter((o) => selectedSet.has(o.value));
  const toggle = (v) => {
    if (selectedSet.has(v)) {
      onChange?.(value.filter((x) => x !== v));
    } else {
      onChange?.([...value, v]);
    }
  };
  const remove = (v, e) => {
    e.stopPropagation();
    onChange?.(value.filter((x) => x !== v));
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between font-normal h-auto min-h-9 py-1.5", className)}
          data-testid={testId}
        >
          <div className="flex flex-wrap gap-1 items-center">
            {selectedOptions.length === 0 && <span className="text-muted-foreground">{placeholder}</span>}
            {selectedOptions.slice(0, maxBadges).map((o) => (
              <Badge key={o.value} variant="secondary" className="text-xs">
                {o.label}
                <span role="button" onClick={(e) => remove(o.value, e)} className="ml-1 cursor-pointer hover:text-rose-600">
                  <X size={10} />
                </span>
              </Badge>
            ))}
            {selectedOptions.length > maxBadges && (
              <Badge variant="secondary" className="text-xs">+{selectedOptions.length - maxBadges} more</Badge>
            )}
          </div>
          <ChevronsUpDown size={14} className="ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={`${o.label} ${o.hint || ""}`}
                  onSelect={() => toggle(o.value)}
                  data-testid={testId ? `${testId}-option-${o.value}` : undefined}
                >
                  <Check size={14} className={cn("mr-2", selectedSet.has(o.value) ? "opacity-100" : "opacity-0")} />
                  <span>{o.label}</span>
                  {o.hint && <span className="ml-auto text-xs text-muted-foreground">{o.hint}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
